import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import './index.scss';

interface RecordLinkProps {
  /** Route of the related record, e.g. `/jobs/abc123`. */
  to: string;
  /** What to show. Falls back to a dash when the record has no label. */
  children: React.ReactNode;
  /** Reads as "Open the job". Used for the accessible name. */
  label?: string;
}

/**
 * A link from one record to a related one.
 *
 * A real anchor rather than a click handler, so cmd-click, middle-click and
 * "open in new tab" all work — the popups this replaces used
 * `window.open(path, '_blank')`, which forced a new tab on every click and
 * gave the keyboard nothing. Plain clicks stay in the SPA.
 */
const RecordLink: React.FC<RecordLinkProps> = ({ to, children, label }) => {
  const navigate = useNavigate();

  return (
    <a
      className="cc-record-link"
      href={to}
      aria-label={label}
      onClick={(e) => {
        // Let the browser handle anything that means "somewhere else".
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        navigate(to);
      }}
    >
      <span className="cc-record-link__text">{children}</span>
      <OpenInNewOutlinedIcon className="cc-record-link__icon" />
    </a>
  );
};

export default RecordLink;
